import { deleteCookie } from "cookies-next"
import { signOut, useSession } from "next-auth/react"
import React from "react"
import { AuthenticateStateChangeType, notifyAuthenticationStateChanged } from "./useExtension"
import { Group } from "platform-common/types"
import { IS_AUTOLOGIN } from "@web/components/Auth/LoginForm"
import { useBillingContext } from "@web/context/BillingContext"
import { setUser } from "@sentry/nextjs"
import { clearCachedJwt } from "@web/api/jwt"

export const handleSignOut = async () => {
  if (typeof window === "undefined") return

  deleteCookie(IS_AUTOLOGIN)
  await notifyAuthenticationStateChanged({ type: AuthenticateStateChangeType.SignedOut })
  clearCachedJwt()
  await signOut()
}

export const useAuthState = () => {
  // get our current server-side user and billing info
  const { data: session, status } = useSession()
  const { billingStatus } = useBillingContext()
  const tomboUserId = session?.user?.tomboUserId

  // RBAC
  const roles = React.useMemo(() => session?.roles || [], [session?.roles])

  // can the user manage their team?
  const userIsTeamManager = React.useMemo(() => {
    if (!session?.user) return false

    // is the user the owner of the team?
    const teamOwnerId = billingStatus?.getBillingStatus?.teamOwnerId
    if (teamOwnerId && tomboUserId && tomboUserId === teamOwnerId) return true

    return roles.includes(Group.HRProManagers) || roles.includes(Group.Admins) || roles.includes(Group.BackOffice)
  }, [session?.user, billingStatus?.getBillingStatus?.teamOwnerId, tomboUserId, roles])

  const userHasBackOfficeAccess = React.useMemo(() => {
    if (!roles?.length) return false
    return roles.includes(Group.BackOffice) || roles.includes(Group.Admins)
  }, [roles])

  // set sentry user
  React.useEffect(() => {
    const user = session?.user
    if (user) {
      setUser({
        id: session.user.id,
        email: session.user.email ?? undefined,
        username: session.user.username,
      })
    }
  }, [session?.user])
  return {
    user: session?.user,
    status,
    signOut: handleSignOut,
    userIsTeamManager,
    userHasBackOfficeAccess,
    roles,
    tomboUserId,
    isImpersonating: Boolean(session?.isImpersonating),
  }
}
